import React, {Suspense} from 'react';

import CloudflareStreamVideoModel from 'coreClasses/cloudflare-stream/CloudflareStreamVideo';
import ThronVideoModel from 'coreClasses/thron/ThronVideo';
import VimeoVideoModel from 'coreClasses/vimeo/VimeoVideo';
import NonCriticalBoundary from 'coreComponents/NonCriticalBoundary';
import useVideoAspectRatio from 'coreHooks/useVideoAspectRatio';

import ShowOrHideOnBreakpoint from 'brandComponents/Responsive/ShowOrHideOnBreakpoint';

import CloudflareStreamVideo from './Subcomponents/CloudflareStreamVideo';
import ThronVideo from './Subcomponents/ThronVideo';
import VimeoVideo from './Subcomponents/VimeoVideo';
import {EProviders, IVideoProps} from './IVideoProps';

const Video = ({
	provider,
	videoId,
	mobileVideoId,
	videoConfig,
	controlsConfig,
	thumbnail,
	thumbnailMobile,
	changeBreakpoint = 'small',
	aspectRatio: aspectRatioProp,
	aspectRatioMobile: aspectRatioMobileProp,
}: IVideoProps) => {
	const {aspectRatio: fetchedAspectRatio, aspectRatioMobile: fetchedAspectRatioMobile} = useVideoAspectRatio({
		provider,
		videoId,
		mobileVideoId,
	});

	const aspectRatio = aspectRatioProp || fetchedAspectRatio;
	const aspectRatioMobile = aspectRatioMobileProp || fetchedAspectRatioMobile;

	switch (provider) {
		case EProviders.THRON: {
			const thronVideo = new ThronVideoModel({
				videoId,
				thumbnail,
				aspectRatio
			});
			const thronVideoMobile = new ThronVideoModel({
				videoId: mobileVideoId || videoId,
				thumbnail: mobileVideoId ? thumbnailMobile : thumbnail,
				aspectRatio: mobileVideoId ? aspectRatioMobile : aspectRatio
			});

			return (
				<>
					<ShowOrHideOnBreakpoint classNames="video" query={`<${changeBreakpoint}`} >
						<ThronVideo
							thronVideo={thronVideoMobile}
							videoConfig={videoConfig}
							controlsConfig={controlsConfig}
						/>
					</ShowOrHideOnBreakpoint>
					<ShowOrHideOnBreakpoint classNames="video" query={`>=${changeBreakpoint}`} >
						<ThronVideo
							thronVideo={thronVideo}
							videoConfig={videoConfig}
							controlsConfig={controlsConfig}
						/>
					</ShowOrHideOnBreakpoint>
				</>
			);
		}

		case EProviders.VIMEO: {
			const vimeoVideo = new VimeoVideoModel({videoId, aspectRatio, thumbnail});
			const vimeoVideoMobile = new VimeoVideoModel({
				videoId: mobileVideoId || videoId,
				aspectRatio: mobileVideoId ? aspectRatioMobile : aspectRatio,
				thumbnail: mobileVideoId ? thumbnailMobile : thumbnail
			});

			return (
				<>
					<ShowOrHideOnBreakpoint classNames="video" query={`<=${changeBreakpoint}`} >
						<VimeoVideo
							vimeoVideo={vimeoVideoMobile}
							videoConfig={videoConfig}
							controlsConfig={controlsConfig}
						/>
					</ShowOrHideOnBreakpoint>
					<ShowOrHideOnBreakpoint classNames="video" query={`>${changeBreakpoint}`}>
						<VimeoVideo
							vimeoVideo={vimeoVideo}
							videoConfig={videoConfig}
							controlsConfig={controlsConfig}
						/>
					</ShowOrHideOnBreakpoint>
				</>
			);
		}

		case EProviders.CLOUDFLARE_STREAM: {
			const cfStreamVideo = new CloudflareStreamVideoModel({
				videoId,
				thumbnail,
				aspectRatio
			});
			const cfStreamVideoMobile = new CloudflareStreamVideoModel({
				videoId: mobileVideoId || videoId,
				thumbnail: mobileVideoId ? thumbnailMobile : thumbnail,
				aspectRatio: mobileVideoId ? aspectRatioMobile : aspectRatio
			});

			return (
				<>
					<ShowOrHideOnBreakpoint classNames="video" query={`<${changeBreakpoint}`} >
						<CloudflareStreamVideo
							cloudflareStreamVideo={cfStreamVideoMobile}
							videoConfig={videoConfig}
							controlsConfig={controlsConfig}
						/>
					</ShowOrHideOnBreakpoint>
					<ShowOrHideOnBreakpoint classNames="video" query={`>=${changeBreakpoint}`} >
						<CloudflareStreamVideo
							cloudflareStreamVideo={cfStreamVideo}
							videoConfig={videoConfig}
							controlsConfig={controlsConfig}
						/>
					</ShowOrHideOnBreakpoint>
				</>
			);
		}

		default:
			return null;
	}
};

/** Default ratio of the players when the provider gives none. */
const DEFAULT_ASPECT_RATIO = '16/9';

/**
 * Space of the video while its aspect ratio is fetched: the ratio of the CMS, if any, otherwise the default of the
 * players (the real one may differ, the box then changes size once).
 */
const VideoPlaceholder = ({aspectRatio, aspectRatioMobile, changeBreakpoint = 'small'}: IVideoProps) => (
	<>
		<ShowOrHideOnBreakpoint classNames="video" query={`<${changeBreakpoint}`}>
			<div style={{aspectRatio: aspectRatioMobile || aspectRatio || DEFAULT_ASPECT_RATIO}} />
		</ShowOrHideOnBreakpoint>
		<ShowOrHideOnBreakpoint classNames="video" query={`>=${changeBreakpoint}`}>
			<div style={{aspectRatio: aspectRatio || DEFAULT_ASPECT_RATIO}} />
		</ShowOrHideOnBreakpoint>
	</>
);

/**
 * The aspect ratio comes from the video provider (`useVideoAspectRatio`, a server request). `nonCritical` (CMS
 * modules): below a `NonCriticalBoundary` it does not hold the shell, the video is streamed after it when the
 * provider is slow (PLAN.md 2b.7c). Otherwise (PDP gallery) the shell waits for it.
 */
export default ({nonCritical = false, ...props}: IVideoProps) => {
	if (!nonCritical) {
		return (
			<Suspense fallback={null}>
				<Video {...props} />
			</Suspense>
		);
	}

	return (
		<NonCriticalBoundary fallback={<VideoPlaceholder {...props} />}>
			<Video {...props} />
		</NonCriticalBoundary>
	);
};